import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Button, Row, Col, Select, InputNumber, Tag, Space, Tree, Popconfirm, Typography, Spin, notification, Card, Input, Alert, Radio, Checkbox
} from 'antd';
import {
  DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, WarningOutlined, CloseOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import lib from '../../assets/utils/lib';
import { metricTagColor } from '../../assets/utils/limitMetricDisplay';
import { PlanLimitsExplainerModal } from './PlanLimitsExplainerModal';
import { PlanLimitFieldGuideModal } from './PlanLimitFieldGuideModal';
import {
  resolveMemberScopeOptions, resolveGenderOptions, resolveAgeBounds, describeEligibilityMembers,
} from '../CarePrograms/careProgramEligibility';

const { Text, Paragraph } = Typography;

const ENFORCEMENT_OPTIONS = [
  { label: 'Disabled', value: 'disabled' },
  { label: 'Advisory (default)', value: 'advisory' },
  { label: 'Strict', value: 'strict' },
];

const SCOPE_OPTIONS = [
  { label: 'Enrollee', value: 'enrollee' },
  { label: 'Policy', value: 'policy' },
];

// Raw subcategory/item targets are intentionally not offered: there's no reliable
// cross-facility item identity to pick from today (see
// docs/PLAN_LIMIT_ITEM_TRACKING_STRATEGY.md in api_v1) — the admin API rejects them.
// standard_item_type is the exception: a bounded, admin-curated taxonomy with
// reliable cross-facility identity via the tagging pipeline, used for sub-category
// limits narrower than a whole costable_category (e.g. "Chronic Disease Medication").
const TARGET_TYPE_OPTIONS = [
  { label: 'Plan', value: 'plan' },
  { label: 'Category', value: 'category' },
  { label: 'Standard Item Type', value: 'standard_item_type' },
];

const PERIOD_OPTIONS = [
  { label: 'Annual', value: 'annual' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Per encounter', value: 'per_encounter' },
];

const formatLimitDisplay = (limit) => {
  const slug = limit.metric_slug || limit.metric_type;
  const unit = limit.unit_label || '';
  const raw = limit.limit_value;
  const val = raw != null ? Number(raw) : null;
  if (val == null || Number.isNaN(val)) return null;
  if (slug === 'monetary' || unit === '₦') {
    return (
      <CurrencyFormat value={val} prefix="₦" thousandSeparator="," displayType="text" />
    );
  }
  const suffix = unit && unit !== '₦' ? ` ${unit}` : '';
  return <Text>{val.toLocaleString()}{suffix}</Text>;
};

/** Mirrors PremiumConsumptionService::limitHasEligibilityRules() — a limit with none
 * of these set (the common case) applies to everyone, same as before eligibility existed. */
const limitHasEligibility = (limit) => Boolean(
  (limit.member_scope && limit.member_scope !== 'all_members')
  || (limit.genders && String(limit.genders).trim() !== '')
  || limit.age_min != null
  || limit.age_max != null,
);

const limitEligibilitySummary = (limit, memberScopeOptions, genderOptions) => describeEligibilityMembers(
  {
    member_scope: limit.member_scope || 'all_members',
    genders: limit.genders ? String(limit.genders).split(',').map((g) => g.trim()).filter(Boolean) : [],
    age_min: limit.age_min,
    age_max: limit.age_max,
  },
  { memberScopeOptions, genderOptions },
);

const buildTreeData = (limits = [], schema = null) => {
  const memberScopeOptions = resolveMemberScopeOptions(schema);
  const genderOptions = resolveGenderOptions(schema);
  const map = {};
  const roots = [];
  limits.forEach((limit) => {
    const slug = limit.metric_slug || limit.metric_type || 'metric';
    const hasEligibility = limitHasEligibility(limit);
    map[limit.id] = {
      key: String(limit.id),
      title: (
        <Space size="small" wrap>
          <Text strong>{limit.target_label || `${limit.target_type} limit`}</Text>
          <Tag color={metricTagColor(slug)}>{limit.metric_name || slug}</Tag>
          <Tag>{limit.scope}</Tag>
          {formatLimitDisplay(limit)}
          {limit.period && <Tag color="purple">{limit.period}</Tag>}
          {limit.parent_id && <Tag color="geekblue">sub-limit</Tag>}
          {hasEligibility && (
            <Tag color="volcano">{limitEligibilitySummary(limit, memberScopeOptions, genderOptions)}</Tag>
          )}
        </Space>
      ),
      children: [],
      _raw: limit,
    };
  });
  limits.forEach((limit) => {
    const node = map[limit.id];
    if (limit.parent_id && map[limit.parent_id]) {
      map[limit.parent_id].children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
};

export const PlanLimitsManager = ({ planId, planName, open, onClose }) => {
  const autoOpenedAddForm = useRef(false);
  const [loading, setLoading] = useState(false);
  const [limits, setLimits] = useState([]);
  const [metricTypes, setMetricTypes] = useState([]);
  const [metricTypesLoadFailed, setMetricTypesLoadFailed] = useState(false);
  const [enforcement, setEnforcement] = useState(null);
  const [enforcementLoading, setEnforcementLoading] = useState(false);

  const [eligibilitySchema, setEligibilitySchema] = useState(null);
  const [eligibilitySchemaLoading, setEligibilitySchemaLoading] = useState(false);

  const [costableCategoryOptions, setCostableCategoryOptions] = useState([]);
  const [loadingCostableCategories, setLoadingCostableCategories] = useState(false);

  const [standardItemTypeOptions, setStandardItemTypeOptions] = useState([]);
  const [loadingStandardItemTypes, setLoadingStandardItemTypes] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [explainerOpen, setExplainerOpen] = useState(false);
  const [fieldGuideOpen, setFieldGuideOpen] = useState(false);

  const selectedMetric = metricTypes.find((m) => Number(m.id) === Number(formData.limit_metric_type_id));

  const fetchLimits = async () => {
    setLoading(true);
    try {
      const resp = await lib.getPlanLimits(planId);
      const data = resp?.data;
      if (data && Number(data?.error) === 0) {
        setLimits(data?.data?.limits || []);
      }
    } catch (e) {
      notification.error({ message: e?.message || 'Failed to load limits' });
    } finally {
      setLoading(false);
    }
  };

  const fetchMetricTypes = async () => {
    setMetricTypesLoadFailed(false);
    try {
      const resp = await lib.getLimitMetricTypes();
      const data = resp?.data;
      if (data && Number(data?.error) === 0) {
        const list = Array.isArray(data?.data) ? data.data : [];
        const active = list.filter((m) => Number(m?.status) === 1);
        setMetricTypes(active);
        setMetricTypesLoadFailed(active.length === 0);
      } else {
        setMetricTypes([]);
        setMetricTypesLoadFailed(true);
      }
    } catch (e) {
      setMetricTypes([]);
      setMetricTypesLoadFailed(true);
      notification.error({ message: e?.message || 'Failed to load limit types (metrics)' });
    }
  };

  const fetchEnforcement = async () => {
    try {
      const resp = await lib.getPlanEnforcement(planId);
      const data = resp?.data;
      if (data && Number(data?.error) === 0) {
        setEnforcement(data?.data?.pa_enforcement_mode || 'advisory');
      }
    } catch (e) {
      // silently fail
    }
  };

  const fetchEligibilitySchema = async () => {
    setEligibilitySchemaLoading(true);
    try {
      const resp = await lib.getAdminCareProgramSchema();
      const data = resp?.data;
      if (data && Number(data?.error) === 0) {
        setEligibilitySchema(data?.data || null);
      }
    } catch (e) {
      // Eligibility is optional — a limit can still be created/edited without it.
    } finally {
      setEligibilitySchemaLoading(false);
    }
  };

  const fetchCostableCategories = async () => {
    setLoadingCostableCategories(true);
    try {
      const resp = await lib.getAdminTariffCategories();
      const body = resp?.data;
      const rows = Array.isArray(body?.data) ? body.data : [];
      setCostableCategoryOptions(rows);
    } catch (e) {
      setCostableCategoryOptions([]);
      notification.warning({ message: e?.message || 'Could not load categories for the target picker' });
    } finally {
      setLoadingCostableCategories(false);
    }
  };

  const fetchStandardItemTypes = async () => {
    setLoadingStandardItemTypes(true);
    try {
      const resp = await lib.getAdminStandardItemTypes();
      const body = resp?.data;
      const rows = Array.isArray(body?.data) ? body.data : [];
      setStandardItemTypeOptions(rows);
    } catch (e) {
      setStandardItemTypeOptions([]);
      notification.warning({ message: e?.message || 'Could not load standard item types for the target picker' });
    } finally {
      setLoadingStandardItemTypes(false);
    }
  };

  const updateEnforcement = async (value) => {
    setEnforcementLoading(true);
    try {
      const resp = await lib.updatePlanEnforcement(planId, { pa_enforcement_mode: value });
      const data = resp?.data;
      if (data && Number(data?.error) === 0) {
        setEnforcement(value);
        notification.success({ message: data?.message || 'Enforcement updated' });
      } else {
        notification.error({ message: data?.message || 'Failed to update enforcement' });
      }
    } catch (e) {
      notification.error({ message: e?.message || 'Failed to update enforcement' });
    } finally {
      setEnforcementLoading(false);
    }
  };

  /** Shared validation + payload shaping for both create and update. Returns null if invalid. */
  const buildLimitPayload = () => {
    if (!formData.limit_metric_type_id || !formData.scope || !formData.target_type || formData.period == null || formData.period === '') {
      notification.warning({ message: 'Please fill all required fields (scope, metric type, target, period)' });
      return null;
    }
    const lv = Number(formData.limit_value);
    if (!Number.isFinite(lv) || lv <= 0) {
      notification.warning({ message: 'Limit value must be a positive number' });
      return null;
    }
    if (formData.target_type !== 'plan' && !editingId) {
      const tid = formData.target_id;
      if (tid == null || tid === '' || Number.isNaN(Number(tid))) {
        notification.warning({ message: 'Select a target (category, plan-category row, or item) or enter a manual target ID' });
        return null;
      }
    }
    if (formData.age_min != null && formData.age_max != null && Number(formData.age_min) > Number(formData.age_max)) {
      notification.warning({ message: 'Eligibility: minimum age cannot be greater than maximum age' });
      return null;
    }
    return {
      scope: formData.scope,
      limit_metric_type_id: Number(formData.limit_metric_type_id),
      limit_value: lv,
      // Always sent explicitly (including null) so clearing a previously-set name
      // works, not just adding one — the API only touches fields present in the body.
      name: formData.name && formData.name.trim() !== '' ? formData.name.trim() : null,
      target_type: formData.target_type,
      checkin_type_id: formData.checkin_type_id != null && formData.checkin_type_id !== '' ? Number(formData.checkin_type_id) : undefined,
      period: formData.period,
      priority: Number(formData.priority || 0),
      parent_id: formData.parent_id || undefined,
      target_id: formData.target_type !== 'plan' ? Number(formData.target_id) : undefined,
      cascade_group: formData.cascade_group || undefined,
      // Always sent explicitly (including null) so editing a limit can clear a
      // previously-set restriction, not just add one — the API only touches fields
      // present in the request body.
      member_scope: formData.member_scope && formData.member_scope !== 'all_members' ? formData.member_scope : null,
      genders: Array.isArray(formData.genders) && formData.genders.length > 0 ? formData.genders : null,
      age_min: formData.age_min != null && formData.age_min !== '' ? Number(formData.age_min) : null,
      age_max: formData.age_max != null && formData.age_max !== '' ? Number(formData.age_max) : null,
    };
  };

  const resetForm = () => {
    setShowAdd(false);
    setFormData({});
    setEditingId(null);
  };

  const handleAddLimit = async () => {
    const payload = buildLimitPayload();
    if (!payload) return;
    setSubmitting(true);
    try {
      const resp = await lib.createPlanLimit(planId, payload);
      const data = resp?.data;
      if (data && Number(data?.error) === 0) {
        notification.success({ message: data?.message || 'Limit created' });
        resetForm();
        fetchLimits();
      } else {
        notification.error({ message: data?.message || 'Failed to create limit' });
      }
    } catch (e) {
      notification.error({ message: e?.message || 'Failed to create limit' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateLimit = async () => {
    const payload = buildLimitPayload();
    if (!payload) return;
    setSubmitting(true);
    try {
      const resp = await lib.updatePlanLimit(planId, editingId, payload);
      const data = resp?.data;
      if (data && Number(data?.error) === 0) {
        notification.success({ message: data?.message || 'Limit updated' });
        resetForm();
        fetchLimits();
      } else {
        notification.error({ message: data?.message || 'Failed to update limit' });
      }
    } catch (e) {
      notification.error({ message: e?.message || 'Failed to update limit' });
    } finally {
      setSubmitting(false);
    }
  };

  /** Populate the form from an existing limit row and open it in edit mode.
   * Target (type/id) is kept read-only during edit — the cascading category/item
   * pickers can't be reliably reconstructed from a bare target_id, and re-targeting
   * an existing limit is rare relative to adjusting its value/period/scope. */
  const handleEditLimit = (raw) => {
    setEditingId(raw.id);
    setFormData({
      scope: raw.scope,
      limit_metric_type_id: raw.limit_metric_type_id,
      limit_value: Number(raw.limit_value),
      name: raw.name ?? '',
      target_type: raw.target_type,
      target_id: raw.target_id ?? undefined,
      target_label: raw.target_label,
      checkin_type_id: raw.checkin_type_id ?? undefined,
      period: raw.period,
      priority: raw.priority ?? 0,
      parent_id: raw.parent_id ?? undefined,
      cascade_group: raw.cascade_group ?? '',
      member_scope: raw.member_scope || 'all_members',
      genders: raw.genders ? String(raw.genders).split(',').map((g) => g.trim()).filter(Boolean) : [],
      age_min: raw.age_min ?? null,
      age_max: raw.age_max ?? null,
    });
    setShowAdd(true);
  };

  const handleDeleteLimit = async (limitId) => {
    setLoading(true);
    try {
      const resp = await lib.deletePlanLimit(planId, limitId);
      const data = resp?.data;
      if (data && Number(data?.error) === 0) {
        notification.success({ message: data?.message || 'Limit deleted' });
        fetchLimits();
      } else {
        notification.error({ message: data?.message || 'Failed to delete limit' });
      }
    } catch (e) {
      notification.error({ message: e?.message || 'Failed to delete limit' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && planId) {
      autoOpenedAddForm.current = false;
      fetchLimits();
      fetchEnforcement();
      fetchMetricTypes();
      fetchCostableCategories();
      fetchStandardItemTypes();
      fetchEligibilitySchema();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planId]);

  /** First time on a plan with no limits, open the add form so "limit type" is visible. */
  useEffect(() => {
    if (!open || loading || limits.length > 0 || autoOpenedAddForm.current) return;
    autoOpenedAddForm.current = true;
    setShowAdd(true);
  }, [open, loading, limits.length]);

  const enforcementColor = (mode) => {
    switch (mode) {
      case 'strict': return 'red';
      case 'advisory': return 'orange';
      case 'disabled': return 'default';
      default: return 'default';
    }
  };

  const enforcementIcon = (mode) => {
    switch (mode) {
      case 'strict': return <CloseOutlined />;
      case 'advisory': return <WarningOutlined />;
      case 'disabled': return <InfoCircleOutlined />;
      default: return null;
    }
  };

  const treeData = buildTreeData(limits, eligibilitySchema);
  const memberScopeOptions = resolveMemberScopeOptions(eligibilitySchema);
  const genderOptions = resolveGenderOptions(eligibilitySchema);
  const ageBounds = resolveAgeBounds(eligibilitySchema);

  const metricOptions = metricTypes
    .slice()
    .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0))
    .map((m) => ({
      value: m.id,
      label: `${m.name} — ${m.slug}${m.unit_label ? ` (${m.unit_label})` : ''}`,
    }));

  const quickMetricPresets = metricTypes.filter((m) => ['monetary', 'count'].includes(String(m.slug || '').toLowerCase()));

  const limitValueStep = selectedMetric?.data_type === 'int' ? 1 : 0.01;

  return (
    <>
    <Modal
      open={open}
      title={<Space>
        <span>{planName} — Limits & Enforcement</span>
        {enforcement && (
          <Tag color={enforcementColor(enforcement)} icon={enforcementIcon(enforcement)}>
            {enforcement.toUpperCase()}
          </Tag>
        )}
        <Button
          type="link"
          size="small"
          icon={<InfoCircleOutlined />}
          onClick={() => setExplainerOpen(true)}
          style={{ paddingLeft: 4, paddingRight: 4 }}
        >
          How this works
        </Button>
        <Button
          type="link"
          size="small"
          icon={<InfoCircleOutlined />}
          onClick={() => setFieldGuideOpen(true)}
          style={{ paddingLeft: 4, paddingRight: 4 }}
        >
          Field guide
        </Button>
      </Space>}
      onCancel={onClose}
      footer={[
        <Button key="reload" onClick={() => { fetchLimits(); fetchCostableCategories(); fetchStandardItemTypes(); }} icon={<ReloadOutlined />} disabled={loading}>Reload</Button>,
        <Button key="add" type="dashed" icon={<PlusOutlined />} onClick={() => { setEditingId(null); setFormData({}); setShowAdd(true); }} disabled={loading}>Add Limit</Button>,
        <Button key="close" type="primary" danger onClick={onClose}>Close</Button>,
      ]}
      width={900}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Enforcement Mode */}
          <Card size="small" title="Enforcement Mode">
            <Row gutter={[12, 12]} align="middle">
              <Col span={16}>
                <Select
                  style={{ width: '100%' }}
                  value={enforcement}
                  onChange={updateEnforcement}
                  options={ENFORCEMENT_OPTIONS}
                  loading={enforcementLoading}
                  disabled={enforcementLoading}
                />
              </Col>
              <Col span={8}>
                <Text type="secondary">
                  {enforcement === 'strict' && 'Hard block when limit exceeded.'}
                  {enforcement === 'advisory' && 'Warn user; allow override with confirmation.'}
                  {enforcement === 'disabled' && 'No enforcement; limits visible only.'}
                </Text>
              </Col>
            </Row>
          </Card>

          {/* Limits Tree */}
          <Card size="small" title={<Space>Limits Definitions <Text type="secondary">({limits.length})</Text></Space>}>
            {!showAdd && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Adding a limit"
                description="Click Add Limit in the footer to choose limit type (amount, visits, bed nights, etc.), scope, where it applies, and the cap value."
              />
            )}
            {treeData.length > 0 ? (
              <Tree
                treeData={treeData}
                defaultExpandAll
                showLine
                titleRender={(node) => (
                  <Space size="small" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <span>{node.title}</span>
                    <Space size={4}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditLimit(node._raw)}
                      />
                      <Popconfirm
                        title="Delete this limit?"
                        description="This will affect all enrollees on this plan."
                        onConfirm={() => handleDeleteLimit(node._raw.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  </Space>
                )}
              />
            ) : (
              <Text type="secondary">No limit definitions yet. Click &quot;Add Limit&quot; to create one.</Text>
            )}
          </Card>

          {/* Add / Edit Form */}
          {showAdd && (
            <Card size="small" title={editingId ? 'Edit Limit' : 'Add New Limit'}>
              <Row gutter={[12, 12]}>
                <Col span={24}>
                  <Text strong>Name (optional)</Text>
                  <Input
                    placeholder="e.g. Outpatient Drug Cap — leave blank to auto-label from metric + target + period"
                    value={formData.name || ''}
                    onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
                  />
                </Col>
                <Col span={24}>
                  <Text strong>Limit type</Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                      What this cap measures (replaces old Monetary / Count). Each row uses one type; add another row for e.g. amount and visits together.
                    </Text>
                    {quickMetricPresets.length > 0 && (
                      <Space wrap size="small" style={{ marginBottom: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Quick pick:</Text>
                        {quickMetricPresets.map((m) => (
                          <Button
                            key={m.id}
                            size="small"
                            type={Number(formData.limit_metric_type_id) === Number(m.id) ? 'primary' : 'default'}
                            onClick={() => setFormData((d) => ({ ...d, limit_metric_type_id: m.id }))}
                          >
                            {m.name}
                          </Button>
                        ))}
                      </Space>
                    )}
                  </div>
                  <Select
                    style={{ width: '100%', maxWidth: 640 }}
                    placeholder="Select limit type (amount, visits, bed nights, …)"
                    value={formData.limit_metric_type_id}
                    onChange={(v) => setFormData((d) => ({ ...d, limit_metric_type_id: v }))}
                    options={metricOptions}
                    showSearch
                    optionFilterProp="label"
                    status={metricOptions.length === 0 && metricTypesLoadFailed ? 'warning' : undefined}
                    notFoundContent={metricOptions.length ? 'No match' : 'No types loaded'}
                  />
                  {metricOptions.length === 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginTop: 8 }}
                      message="Limit types did not load"
                      description="Check network and that your account can call GET /v2/admin/limit-metric-types (e.g. plans.manage). Without this list you cannot pick a type."
                    />
                  )}
                </Col>
                <Col span={8}>
                  <Text strong>Scope</Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select scope"
                    value={formData.scope}
                    onChange={(v) => setFormData((d) => ({ ...d, scope: v }))}
                    options={SCOPE_OPTIONS}
                  />
                </Col>
                <Col span={8}>
                  <Text strong>Target type</Text>
                  {editingId ? (
                    <div style={{ marginTop: 4 }}>
                      <Tag>{formData.target_type}</Tag>
                      {formData.target_label && <Text type="secondary"> — {formData.target_label}</Text>}
                    </div>
                  ) : (
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Select target"
                      value={formData.target_type}
                      onChange={(v) => setFormData((d) => ({
                        ...d,
                        target_type: v,
                        target_id: undefined,
                      }))}
                      options={TARGET_TYPE_OPTIONS}
                    />
                  )}
                </Col>
                <Col span={8}>
                  <Text strong>Period</Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select period"
                    value={formData.period}
                    onChange={(v) => setFormData((d) => ({ ...d, period: v }))}
                    options={PERIOD_OPTIONS}
                  />
                </Col>

                {!editingId && formData.target_type === 'category' && (
                  <Col span={24}>
                    <Text strong>Category</Text>
                    <Select
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="Choose a category"
                      loading={loadingCostableCategories}
                      showSearch
                      optionFilterProp="label"
                      value={formData.target_id}
                      onChange={(v) => setFormData((d) => ({ ...d, target_id: v }))}
                      options={costableCategoryOptions.map((c) => ({
                        value: Number(c.id),
                        label: c.category_name || `Category ${c.id}`,
                      }))}
                    />
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      Sends a <code>costable_category</code> id as <code>target_id</code> — this is
                      the category actually recorded against consumption (Drugs / Services /
                      Laboratory / Radiological), not the plan-designer benefit category.
                    </Text>
                  </Col>
                )}

                {!editingId && formData.target_type === 'standard_item_type' && (
                  <Col span={24}>
                    <Text strong>Standard item type</Text>
                    <Select
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="Choose a standard item type"
                      loading={loadingStandardItemTypes}
                      showSearch
                      optionFilterProp="label"
                      value={formData.target_id}
                      onChange={(v) => setFormData((d) => ({ ...d, target_id: v }))}
                      options={standardItemTypeOptions.map((t) => ({
                        value: Number(t.id),
                        label: t.name || `Item Type ${t.id}`,
                      }))}
                    />
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      Sends a <code>standard_item_types</code> id as <code>target_id</code> — use this
                      for sub-category limits narrower than a whole costable_category, e.g. "Chronic
                      Disease Medication" or a specific ward/ICU accommodation tier.
                    </Text>
                  </Col>
                )}

                <Col span={8}>
                  <Text strong>Limit value</Text>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder={selectedMetric ? `Max (${selectedMetric.unit_label || selectedMetric.slug})` : 'Amount or count'}
                    min={0.01}
                    step={limitValueStep}
                    value={formData.limit_value}
                    onChange={(v) => setFormData((d) => ({ ...d, limit_value: v }))}
                  />
                  {selectedMetric?.description && (
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>{selectedMetric.description}</Text>
                  )}
                </Col>
                <Col span={8}>
                  <Text strong>Check-in type (optional)</Text>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Leave empty = all types"
                    min={1}
                    value={formData.checkin_type_id}
                    onChange={(v) => setFormData((d) => ({ ...d, checkin_type_id: v }))}
                  />
                </Col>
                <Col span={8}>
                  <Text strong>Priority</Text>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0 = highest"
                    min={0}
                    value={formData.priority}
                    onChange={(v) => setFormData((d) => ({ ...d, priority: v }))}
                  />
                </Col>
                <Col span={12}>
                  <Text strong>Cascade group (optional)</Text>
                  <Input
                    placeholder="Group name for related limits"
                    value={formData.cascade_group || ''}
                    onChange={(e) => setFormData((d) => ({ ...d, cascade_group: e.target.value }))}
                  />
                </Col>
                <Col span={12}>
                  <Text strong>Parent limit ID (optional)</Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select parent for sub-limit"
                    allowClear
                    value={formData.parent_id}
                    onChange={(v) => setFormData((d) => ({ ...d, parent_id: v }))}
                    options={limits
                      .filter((l) => l.id !== editingId)
                      .map((l) => ({
                        label: `${l.metric_name || l.metric_slug || 'Limit'} — ${l.target_label || l.id}`,
                        value: l.id,
                      }))}
                  />
                </Col>

                <Col span={24}>
                  <Text strong>Eligibility (optional)</Text>
                  <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 2, marginBottom: 8 }}>
                    Narrows who this limit applies to. Leave as &quot;All enrollees&quot; with no gender/age set for a limit that applies to everyone (today&apos;s default, and the common case). Uses the same eligibility rules as Care Programs.
                  </Paragraph>
                  {eligibilitySchemaLoading ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>Loading eligibility options…</Text>
                  ) : memberScopeOptions.length === 0 ? (
                    <Alert
                      type="warning"
                      showIcon
                      message="Eligibility options unavailable"
                      description="Could not load member scope / gender options from GET /v2/admin/care-programs/schema. You can still save this limit — it will apply to everyone."
                    />
                  ) : (
                    <Row gutter={[12, 12]}>
                      <Col span={24}>
                        <Radio.Group
                          value={formData.member_scope || 'all_members'}
                          onChange={(ev) => setFormData((d) => ({ ...d, member_scope: ev.target.value }))}
                          options={memberScopeOptions.map((o) => ({ label: o.label, value: o.value }))}
                          optionType="button"
                          buttonStyle="solid"
                          size="small"
                        />
                      </Col>
                      <Col xs={24} sm={8}>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Gender</Text>
                        <Checkbox.Group
                          value={formData.genders || []}
                          onChange={(vals) => setFormData((d) => ({ ...d, genders: vals }))}
                          options={genderOptions.map((g) => ({ label: g.label, value: g.value }))}
                        />
                      </Col>
                      <Col xs={12} sm={8}>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Min age</Text>
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="No minimum"
                          min={ageBounds.min}
                          max={ageBounds.max}
                          value={formData.age_min}
                          onChange={(v) => setFormData((d) => ({ ...d, age_min: v ?? null }))}
                        />
                      </Col>
                      <Col xs={12} sm={8}>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Max age</Text>
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="No maximum"
                          min={ageBounds.min}
                          max={ageBounds.max}
                          value={formData.age_max}
                          onChange={(v) => setFormData((d) => ({ ...d, age_max: v ?? null }))}
                        />
                      </Col>
                    </Row>
                  )}
                </Col>

                <Col span={24}>
                  <Space>
                    <Button type="primary" onClick={editingId ? handleUpdateLimit : handleAddLimit} loading={submitting}>Save</Button>
                    <Button onClick={resetForm}>Cancel</Button>
                  </Space>
                </Col>
              </Row>
            </Card>
          )}
        </Space>
      </Spin>
    </Modal>
    <PlanLimitsExplainerModal open={explainerOpen} onClose={() => setExplainerOpen(false)} />
    <PlanLimitFieldGuideModal open={fieldGuideOpen} onClose={() => setFieldGuideOpen(false)} />
    </>
  );
};

export default PlanLimitsManager;
